// skills/gatherer/gathererData.js
// Gathering zones, tools, and configuration

// Gathering zones - 9 zones across 3 tiers
// Rare drop chances reduced - awakening materials should be rare
export const GATHERING_ZONES = {
    // Tier 1 - Basic Pet Food + Twisted Root Fiber
    SHALLOW_GROVES: {
        id: 'shallow_groves',
        name: 'Shallow Groves',
        tier: 1,
        level: 1,
        xp: 15,
        gatherTime: 3000,
        foodYield: 'high',
        rareChance: 0.01,
        color: '#8fbc8f'
    },
    ROOTBOUND_FIELDS: {
        id: 'rootbound_fields',
        name: 'Rootbound Fields',
        tier: 1,
        level: 1,
        xp: 20,
        gatherTime: 4000,
        foodYield: 'medium',
        rareChance: 0.025,
        color: '#9acd32'
    },
    TANGLED_GROWTH: {
        id: 'tangled_growth',
        name: 'Tangled Growth',
        tier: 1,
        level: 10,
        xp: 30,
        gatherTime: 6000,
        foodYield: 'low',
        rareChance: 0.05,
        color: '#6b8e23'
    },

    // Tier 2 - Refined Pet Food + Living Arcane Husk
    ICHOR_FLATS: {
        id: 'ichor_flats',
        name: 'Ichor Flats',
        tier: 2,
        level: 25,
        xp: 45,
        gatherTime: 6000,
        foodYield: 'high',
        rareChance: 0.015,
        color: '#9932cc'
    },
    LIVING_THICKETS: {
        id: 'living_thickets',
        name: 'Living Thickets',
        tier: 2,
        level: 35,
        xp: 55,
        gatherTime: 8000,
        foodYield: 'medium',
        rareChance: 0.035,
        color: '#8b008b'
    },
    WARPED_HOLLOWS: {
        id: 'warped_hollows',
        name: 'Warped Hollows',
        tier: 2,
        level: 45,
        xp: 70,
        gatherTime: 10000,
        foodYield: 'low',
        rareChance: 0.06,
        color: '#663399'
    },

    // Tier 3 - Condensed Pet Food + Condensed Arcane Core Fragments
    COREGROWTH_FIELDS: {
        id: 'coregrowth_fields',
        name: 'Coregrowth Fields',
        tier: 3,
        level: 55,
        xp: 85,
        gatherTime: 10000,
        foodYield: 'medium',
        rareChance: 0.025,
        color: '#00ced1'
    },
    BREACH_ZONES: {
        id: 'breach_zones',
        name: 'Breach Zones',
        tier: 3,
        level: 70,
        xp: 110,
        gatherTime: 12000,
        foodYield: 'low',
        rareChance: 0.05,
        color: '#20b2aa'
    },
    UNSTABLE_GROWTHS: {
        id: 'unstable_growths',
        name: 'Unstable Growths',
        tier: 3,
        level: 85,
        xp: 150,
        gatherTime: 15000,
        foodYield: 'veryLow',
        rareChance: 0.08,
        color: '#008b8b'
    }
};

// Gathering tools
export const GATHERING_TOOLS = {
    WORN_SATCHEL: {
        id: 'worn_satchel',
        name: 'Worn Satchel',
        level: 1,
        speedMultiplier: 1.0,
        price: 0 // Starting tool
    },
    STURDY_BOOTS: {
        id: 'sturdy_boots',
        name: 'Sturdy Boots',
        level: 15,
        speedMultiplier: 1.2,
        price: 1000
    },
    FORAGERS_CLIPPERS: {
        id: 'foragers_clippers',
        name: "Forager's Clippers",
        level: 30,
        speedMultiplier: 1.4,
        price: 5000
    },
    REINFORCED_PACK: {
        id: 'reinforced_pack',
        name: 'Reinforced Pack',
        level: 45,
        speedMultiplier: 1.6,
        price: 25000
    },
    EARTHSHAPER_SHOVEL: {
        id: 'earthshaper_shovel',
        name: 'Earthshaper Shovel',
        level: 60,
        speedMultiplier: 1.8,
        price: 100000
    },
    VERDANT_HARVESTER: {
        id: 'verdant_harvester',
        name: 'Verdant Harvester',
        level: 75,
        speedMultiplier: 2.0,
        price: 250000
    }
};

// Tier-based drop tables
// Higher tiers have a chance to also drop lower tier foods
// Bonus materials provide crops, herbs, and cloth as secondary drops
export const TIER_DROPS = {
    1: {
        food: 'basic_pet_food',
        rare: 'twisted_root_fiber',
        bonusFood: [], // No bonus for tier 1
        // Tier 1 bonus materials: early game crops, herbs, cloth, bones
        bonusMaterials: {
            chance: 0.15, // 15% chance per gather to get a bonus material
            items: [
                // Crops (tiers 1-3 of 9)
                { itemId: 'potato', weight: 3 },
                { itemId: 'onion', weight: 2 },
                { itemId: 'cabbage', weight: 1 },
                // Grimy herbs (tiers 1-2 of 13)
                { itemId: 'grimy_greenleaf', weight: 2 },
                { itemId: 'grimy_duskleaf', weight: 1 },
                // Cloth (tier 1 of 6)
                { itemId: 'linenweave', weight: 1 },
                // Hides (tier 1 of 6)
                { itemId: 'treated_hide', weight: 1 },
                // Bones (tier 1 of 5) - helps early Pyre rituals
                { itemId: 'small_bone', weight: 1 }
            ]
        }
    },
    2: {
        food: 'refined_pet_food',
        rare: 'living_arcane_husk',
        bonusFood: [
            { itemId: 'basic_pet_food', chance: 0.25 } // 25% chance for bonus basic
        ],
        // Tier 2 bonus materials: mid game crops, herbs, cloth, bones
        bonusMaterials: {
            chance: 0.15,
            items: [
                // Crops (tiers 4-5 of 9)
                { itemId: 'tomato', weight: 2 },
                { itemId: 'corn', weight: 1 },
                // Grimy herbs (tiers 3-5 of 13)
                { itemId: 'grimy_ironbloom', weight: 2 },
                { itemId: 'grimy_emberroot', weight: 1.5 },
                { itemId: 'grimy_sanctum_leaf', weight: 1 },
                // Cloth (tiers 2-3 of 6)
                { itemId: 'silkweave', weight: 1.5 },
                { itemId: 'aetherweave', weight: 1 },
                // Hides (tiers 2-3 of 6)
                { itemId: 'green_wyrm_hide', weight: 1.5 },
                { itemId: 'blue_wyrm_hide', weight: 1 },
                // Bones (tiers 1-2 of 5) - helps mid Pyre rituals
                { itemId: 'small_bone', weight: 1.5 },
                { itemId: 'charred_bone', weight: 1 }
            ]
        }
    },
    3: {
        food: 'condensed_pet_food',
        rare: 'condensed_arcane_core_fragment',
        bonusFood: [
            { itemId: 'basic_pet_food', chance: 0.20 },   // 20% chance for bonus basic
            { itemId: 'refined_pet_food', chance: 0.15 }  // 15% chance for bonus refined
        ],
        // Tier 3 bonus materials: upper mid crops, herbs, cloth
        // Top tiers reserved for Combat (Brightmaw+, Stonebark+, Shadowweave+)
        bonusMaterials: {
            chance: 0.15,
            items: [
                // Crops (tiers 6-7 of 9)
                { itemId: 'strawberry', weight: 2 },
                { itemId: 'watermelon', weight: 1 },
                // Grimy herbs (tiers 6-7 of 13)
                { itemId: 'grimy_sunpetal', weight: 2 },
                { itemId: 'grimy_deepvine', weight: 1 },
                // Cloth (tier 4 of 6)
                { itemId: 'runebound_cloth', weight: 1 },
                // Hides (tier 4 of 6)
                { itemId: 'red_wyrm_hide', weight: 1 }
            ]
        }
    }
};

// Food yield quantities based on zone type
export const FOOD_YIELDS = {
    high: { min: 2, max: 4 },
    medium: { min: 1, max: 3 },
    low: { min: 1, max: 2 },
    veryLow: { min: 1, max: 1 }
};

export default {
    GATHERING_ZONES,
    GATHERING_TOOLS,
    TIER_DROPS,
    FOOD_YIELDS
};
